import "./Popover.scss"; // TODO extract transition styles

import React, { ReactNode, useCallback, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";

interface Props<E extends HTMLElement> {
  isPresented: boolean;
  onOutsideDismissTap: () => void;
  onDidDisappear: () => void;
  children: (interiorRef: React.Ref<E>) => ReactNode;
}

export default function Overlay<E extends HTMLElement>(props: Props<E>) {
  const { isPresented, onOutsideDismissTap, onDidDisappear, children } = props;

  const enterTime = useRef<number | null>(null);
  const interiorRef = useRef<E | null>(null);
  const touchState = useRef<{
    identifier: number;
    initialScreenX: number;
    initialScreenY: number;
  } | null>(null);

  const handlePossibleDismissEvent = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const interiorElement = interiorRef.current;
      if (
        enterTime.current &&
        Date.now() - enterTime.current > 2000 &&
        onOutsideDismissTap &&
        interiorElement &&
        event.target instanceof Node &&
        !interiorElement.contains(event.target)
      ) {
        onOutsideDismissTap();
      }
    },
    [onOutsideDismissTap],
  );

  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (touchState.current === null && event.targetTouches.length === 1) {
      const touch = event.targetTouches[0];
      touchState.current = {
        identifier: touch.identifier,
        initialScreenX: touch.screenX,
        initialScreenY: touch.screenY,
      };
      event.preventDefault();
    }
  }, []);

  const handleTouchCancel = useCallback((event: TouchEvent) => {
    const currentTouchState = touchState.current;
    if (
      currentTouchState &&
      event.changedTouches.length === 1 &&
      event.changedTouches[0].identifier === currentTouchState.identifier
    ) {
      touchState.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      const currentTouchState = touchState.current;
      if (
        currentTouchState &&
        event.changedTouches.length === 1 &&
        event.changedTouches[0].identifier === currentTouchState.identifier
      ) {
        handleTouchCancel(event);
        handlePossibleDismissEvent(event);
      }
    },
    [handleTouchCancel, handlePossibleDismissEvent],
  );

  useEffect(() => {
    if (!onOutsideDismissTap || !isPresented) return;

    if ("ontouchend" in window) {
      document.addEventListener("touchstart", handleTouchStart);
      document.addEventListener("touchend", handleTouchEnd);
      document.addEventListener("touchcancel", handleTouchCancel);
    }
    document.addEventListener("click", handlePossibleDismissEvent);
    return () => {
      if ("ontouchend" in window) {
        document.removeEventListener("touchstart", handleTouchStart);
        document.removeEventListener("touchend", handleTouchEnd);
        document.removeEventListener("touchcancel", handleTouchCancel);
      }
      document.removeEventListener("click", handlePossibleDismissEvent);
    };
  }, [
    handlePossibleDismissEvent,
    onOutsideDismissTap,
    isPresented,
    handleTouchStart,
    handleTouchEnd,
    handleTouchCancel,
  ]);

  return (
    <CSSTransition
      in={isPresented}
      timeout={2000}
      classNames="PopoverTransition"
      appear
      onEnter={useCallback(() => (enterTime.current = Date.now()), [])}
      onExited={onDidDisappear}
    >
      {children(interiorRef)}
    </CSSTransition>
  );
}
